/**
 * Filter for employee actions in mobile and desktop
 */
import axios, { AxiosError } from 'axios';
import { computed } from 'vue';
import { useStore } from 'vuex';
import WCSimpleToast from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import ToastService from '@/modules/toasts/services/ToastService';
import ModalService from '@/modules/modals/services/modal.service';
import { MODAL_EVENTS } from '@/constants/EventConstants';
import WCResetIndexModal from '@/components/WCResetIndexModal/WCResetIndexModal.vue';
import WebCartUser from '@/models/entities/user/webCartUser';

export default function useEmployee() {
  const store = useStore();

  const isCustomer = computed(() => store.getters['user/isCustomer']);
  const isEmployee = computed(() => store.getters['user/isEmployee']);
  const user = computed<WebCartUser>(() => store.getters['user/getUser']);

  const downloadLogPath = 'api/employee/logs.zip';

  const isNotAnEmployee = computed<boolean>(() => {
    return !isEmployee.value || isCustomer.value;
  });

  const replicateNow = async (): Promise<void> => {
    try {
      const response = await axios.get('api/employee/rn', { timeout: 120000 });
      ToastService.open(WCSimpleToast, {
        props: {
          variant: 'success',
          title: 'Data Replication',
          message: response.data.message,
        },
        timeout: 7000,
        actions: {},
        skipHash: false,
      });
    } catch (error) {
      ToastService.open(WCSimpleToast, {
        props: {
          variant: 'danger',
          title: 'Error',
          message:
            (error as AxiosError<{ message?: string }>)?.response?.data?.message ||
            (error as Error).message,
        },
        timeout: 7000,
        actions: {},
        skipHash: false,
      });
    }
  };

  const resetIndex = async (): Promise<void> => {
    try {
      const response = await ModalService.open(WCResetIndexModal);
      if (response === MODAL_EVENTS.CONFIRM) {
        await axios.post('api/employee/reset-index');
      }
    } catch (error) {
      console.error(error);
      if ((error as AxiosError)?.response?.data && error.response?.data?.message) {
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: 'Error',
            message: error.response?.data?.message,
          },
          timeout: 10000,
          actions: {},
          skipHash: false,
        });
      }
    }
  };

  return {
    isCustomer,
    isEmployee,
    user,
    downloadLogPath,
    isNotAnEmployee,
    replicateNow,
    resetIndex,
  };
}
